import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Panels and cards.
 *
 * Flat at rest: hierarchy comes from space and type weight, not from outlining
 * every box. A shadow means "this floats above the page", so only popovers and
 * modals have one - and a card that borrows one is claiming to float when it
 * does not.
 */
export function Panel({
  children,
  className,
  as: Tag = 'section',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article' | 'aside';
} & Record<string, unknown>): JSX.Element {
  return (
    <Tag data-component="Panel" className={cx('rounded-card bg-surface', className)} {...rest}>
      {children}
    </Tag>
  );
}

/** A hairline. Separation, not information - which is why it is not measured. */
export function Rule({
  strong = false,
  className,
  ...rest
}: { strong?: boolean; className?: string } & HTMLAttributes<HTMLHRElement>): JSX.Element {
  return (
    <hr
      {...rest}
      data-component="Rule"
      className={cx('border-0 border-t', strong ? 'border-t-rule-strong' : 'border-t-rule', className)}
    />
  );
}

/**
 * A section heading.
 *
 * 11px uppercase with the tracking the design system gives that one size. It
 * is the only place letter-spacing is applied at all.
 */
export function SectionLabel({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return (
    <h2
      {...rest}
      data-component="SectionLabel"
      className={cx('text-micro uppercase tracking-label text-ink-label', className)}
    >
      {children}
    </h2>
  );
}

/**
 * The one serif moment.
 *
 * A named component rather than a free utility, so a second one on the same
 * screen is visible in review - and the render check counts them.
 */
export function SerifMoment({
  children,
  className,
  as: Tag = 'h1',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'p';
} & HTMLAttributes<HTMLElement>): JSX.Element {
  return (
    <Tag
      {...rest}
      data-component="SerifMoment"
      className={cx('serif-moment text-h2 text-ink', className)}
    >
      {children}
    </Tag>
  );
}
